import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Col,
  FormFeedback,
  Input,
  Row,
} from 'reactstrap';
import { useIntl } from 'react-intl';
import Select from 'react-select';

import { isValidEmail } from '../../../../helpers/Validators';

import AccessModalSelectItem from '../../../Access/ModalSelectItem';
import HelpIcon from '../../../HelpIcon';
import SettingsEditButtons from '../EditButtons';

const SettingsOwnerDetails = ({
  profile,
  saving,
  handleSave,
  unsaved,
  billingEmails,
  handleBillingEmails,
  selectedWorkspaceOwner,
  handleSelectedWorkspaceOwner,
  users,
  usersLoading,
}) => {
  const intl = useIntl();
  const [billingEmailInputValue, setBillingEmailInputValue] = useState('');
  const [errors, setErrors] = useState({});

  const handleAddBillingEmail = () => {
    setErrors({});
    if (!billingEmails.includes(billingEmailInputValue)) {
      if (isValidEmail(billingEmailInputValue)) {
        handleBillingEmails([...billingEmails, billingEmailInputValue]);
        setBillingEmailInputValue('');
      } else {
        setErrors({ email: intl.formatMessage({ defaultMessage: 'Please provide valid email address' }) });
      }
    }
  };

  const handleRemoveBillingEmail = (email) => {
    const newBillingEmails = billingEmails.filter(element => element !== email);
    handleBillingEmails(newBillingEmails);
  };

  const handleEnterEmailAddress = (e) => {
    if (e.key === 'Enter') {
      handleAddBillingEmail();
    }
  };

  return (
    <>
      <div className="settings-inner-content">
        <Row>
          <Col>
            <h3>{intl.formatMessage({ defaultMessage: 'Owner details' })}</h3>
          </Col>
        </Row>
        <Row id="workspace-owner" className="workspace-owner-row">
          <Col xl={6}>
            <h4>
              {intl.formatMessage({ defaultMessage: 'Workspace owner' })}
            </h4>
            <div className="workspace-owner-selector">
              <Select
                id="workspace-owner-selector"
                inputId="workspace-owner-selector-input"
                name="workspace-owner"
                className="user-select"
                classNamePrefix="user-select"
                placeholder={intl.formatMessage({ defaultMessage: 'Select user to be the Workspace owner' })}
                noOptionsMessage={() => ('No users')}
                getOptionValue={option => option.uuid}
                getOptionLabel={option => option.name}
                components={{ Option: AccessModalSelectItem }}
                isDisabled={usersLoading || !profile.is_admin}
                isLoading={usersLoading}
                options={users}
                onChange={(value) => handleSelectedWorkspaceOwner(value)}
                value={selectedWorkspaceOwner}
              />
              {!profile.is_admin && (
              <HelpIcon id="workspace-owner-tooltip">
                {intl.formatMessage({ defaultMessage: 'Only Admin can change the Workspace owner' })}
              </HelpIcon>
              )}
            </div>
          </Col>
        </Row>
        <Row id="billing-emails" className="billing-emails-row">
          <Col xl={6}>
            <h4>
              {intl.formatMessage({ defaultMessage: 'Billing emails' })}
            </h4>
            <div className="tags-list">
              {billingEmails.map(email => (
                <div className="tag" key={email}>
                  <span className="tag-label">{email}</span>
                  {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                  <span
                    className="tag-remove"
                    title={intl.formatMessage({ defaultMessage: 'Remove' })}
                    role="button"
                    tabIndex="0"
                    onClick={() => {
                      handleRemoveBillingEmail(email);
                    }}
                  >
                    <i className="far fa-times" />
                  </span>
                  {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                </div>
              ))}
            </div>
            <div className="billing-emails-input-area">
              <Input
                type="email"
                id="fileUuid"
                name="fileUuid"
                className="file-uuid"
                placeholder={intl.formatMessage({ defaultMessage: 'Enter a billing email' })}
                value={billingEmailInputValue}
                onKeyDown={() => setErrors({})}
                onChange={(e) => setBillingEmailInputValue(e.target.value)}
                invalid={Boolean(errors.email)}
                onKeyUp={handleEnterEmailAddress}
              />
              <Button className="btn btn-primary" onClick={handleAddBillingEmail}>
                <i className="fas fa-plus" />
              </Button>
              <br />
            </div>
            {errors.email && (
            <FormFeedback
              style={{
                display: errors.email ? 'block' : 'none',
              }}
            >
              {errors.email}
            </FormFeedback>
            )}
          </Col>
        </Row>
      </div>
      <SettingsEditButtons
        saving={saving}
        handleSave={handleSave}
        unsaved={unsaved}
      />
    </>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  settings: state.App.settings,
});

SettingsOwnerDetails.propTypes = {
  profile: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  selectedWorkspaceOwner: PropTypes.object.isRequired,
  handleSelectedWorkspaceOwner: PropTypes.func.isRequired,
  billingEmails: PropTypes.array.isRequired,
  handleBillingEmails: PropTypes.func.isRequired,
  unsaved: PropTypes.bool.isRequired,
  usersLoading: PropTypes.bool.isRequired,
  users: PropTypes.array.isRequired,
};

export default connect(mapStateToProps)(SettingsOwnerDetails);
